import React, {useContext, useEffect, useState, useRef, UIEvent} from 'react';
import debounce from 'lodash/debounce';

import {
    TrackingContext,
    TrackingContextValue,
} from '@homeaway/react-event-tracking';
import {CHEVRON_LEFT__16, CHEVRON_RIGHT__16} from '@homeaway/svg-defs';
import {SVGIcon} from '@homeaway/react-svg';
import classnames from 'classnames';

import FilterBarLoading from '../FilterBarLoading/FilterBarLoading';
import FilterGroupPill from '../FilterGroupPill/FilterGroupPill';
import MoreFiltersModal from '../MoreFiltersModal/MoreFiltersModal';
import {NUM_FILTER_PILLS_DISPLAYED} from '../../utils/constants';
import {FILTER_BAR_PRESENTED} from '../../utils/edap';

import './FilterBar.less';

export const SCROLL_WIDTH = 300;

const isDropdown = (target: HTMLElement) =>
    target && target.className && target.className.includes('Dropdown');

// The types in this file are listed here rather than being imported from other files
// This is intentional and necessary in order to generate documentation using `npm run start:docs`
// Per react-docgen (https://github.com/reactjs/react-docgen):
// "react-docgen will not be able to grab the type definition if the type is imported or declared in a different file."

type Filter = {
    description?: string;
    refineByQueryArgument: string;
    id: string;
    groupId?: string;
    name: string;
};

export type Refinement = {
    count?: number;
    checked: boolean;
    filter: Filter;
};

type FilterGroupInfo = {
    id: string;
    name: string;
};

export type FilterGroupType = {
    groupInfo: FilterGroupInfo;
    filters: Refinement[];
};

type CustomFilterParams = {
    selectedFilterByTotalPrice: boolean;
    appliedFilterByTotalPrice: boolean;
    appliedMaxNightlyPrice: number | null;
    appliedMinNightlyPrice: number | null;
    selectedMaxNightlyPrice: number | null;
    selectedMinNightlyPrice: number | null;
    selectedMaxTotalPrice: number | null;
    selectedMinTotalPrice: number | null;
    appliedMaxTotalPrice: number | null;
    appliedMinTotalPrice: number | null;
    selectedMinBedrooms: number;
    selectedMinBathrooms: number;
    appliedMinBedrooms: number;
    appliedMinBathrooms: number;
};

export interface FilterBarProps {
    appliedFilters: string[];
    currency: string;
    customFiltersParams: CustomFilterParams;
    displayMoreFiltersPillLeft?: boolean;
    displayNumGroupsAsPills?: number;
    filterGroups: FilterGroupType[];
    isMobile: boolean;
    onClear: Function;
    onCustomFilterUpdate: Function;
    onDismiss: Function;
    onDone: Function;
    onFilterToggle: Function;
    onMultipleFilterToggle: Function;
    priceFilterOptions?: {
        isFilterByTotalPriceEnabled?: boolean;
        lengthOfStay?: number;
        showFilterByPriceType?: boolean;
    };
    priceInputValuesAreValid: boolean;
    results: number;
    selectedFilters: string[];
    showAllFiltersPill: boolean;
    showMoreFiltersPill: boolean;
    updatePriceInputValuesValidity: Function;
}

// eslint-disable-next-line complexity
export default function FilterBar({
    appliedFilters,
    currency,
    customFiltersParams,
    displayMoreFiltersPillLeft = false,
    displayNumGroupsAsPills = NUM_FILTER_PILLS_DISPLAYED,
    filterGroups = [],
    isMobile,
    onClear,
    onCustomFilterUpdate,
    onDismiss,
    onDone,
    onFilterToggle,
    onMultipleFilterToggle,
    priceFilterOptions = {},
    priceInputValuesAreValid,
    results,
    selectedFilters,
    showAllFiltersPill = false,
    showMoreFiltersPill = true,
    updatePriceInputValuesValidity,
}: FilterBarProps) {
    const edapContext: TrackingContextValue = useContext(TrackingContext);
    const filterBarContentRef = useRef<HTMLDivElement>(null);
    const [isHover, setHover] = useState(false);
    const [offsetLeft, setOffsetLeft] = useState(0);
    const [isScrolling, setScroll] = useState(false);
    const isLoading = filterGroups.length === 0;
    const topFilterGroups = filterGroups.slice(0, displayNumGroupsAsPills);

    useEffect(() => {
        /* istanbul ignore else */
        if (!isLoading) {
            edapContext.trigger(FILTER_BAR_PRESENTED, {
                filtergroupid: topFilterGroups
                    .map((filterGroup) => filterGroup.groupInfo.id)
                    .join(','),
            });
        }
    }, [isLoading]);

    const hoverInit = () => {
        const node = filterBarContentRef.current;
        if (node) {
            setOffsetLeft(node.scrollLeft);
            if (node.scrollWidth > window.innerWidth) {
                setHover(true);
            }
        }
    };

    const handlePaginationClick = (dir: string) => {
        const node = filterBarContentRef.current;
        if (node) {
            const newScrollLeft =
                dir === 'left'
                    ? node.scrollLeft - SCROLL_WIDTH
                    : node.scrollLeft + SCROLL_WIDTH;

            // browser supports native smoothscroll
            if ('scrollBehavior' in document.documentElement.style) {
                node.scrollTo({
                    left: newScrollLeft,
                    top: 0,
                    behavior: 'smooth',
                });
            } else {
                node.scrollLeft = newScrollLeft;
            }
            setOffsetLeft(newScrollLeft);
        }
    };

    const handleOnMouseLeave = () => {
        setHover(false);
    };

    const handleOnBlur = () => {
        setHover(false);
    };

    const handleScrollDebounced = debounce(
        () => {
            if (isScrolling) {
                setScroll(false);
            } else {
                setScroll(true);
            }
        },
        100,
        {leading: !isScrolling, trailing: isScrolling}
    );

    const handleScroll = (event: UIEvent<HTMLElement>) => {
        const target = event.target as HTMLElement;
        if (isMobile || isDropdown(target)) {
            return;
        }

        handleScrollDebounced();
    };

    const sharedProps = {
        appliedFilters,
        customFiltersParams,
        currency,
        isMobile,
        onClear,
        onCustomFilterUpdate,
        onDismiss,
        onDone,
        onFilterToggle,
        onMultipleFilterToggle,
        priceFilterOptions,
        priceInputValuesAreValid,
        selectedFilters,
        updatePriceInputValuesValidity,
        results,
    };

    const filtersPaginationClassnames = classnames('FilterBar__pagination', {
        'FilterBar__pagination--hover': isHover,
        'FilterBar__pagination--bound-left': offsetLeft <= 0,
        'FilterBar__pagination--bound-right':
            filterBarContentRef.current &&
            offsetLeft + filterBarContentRef.current.offsetWidth >=
                filterBarContentRef.current.scrollWidth,
    });

    const renderFiltersPill = () => {
        return (
            <>
                {showAllFiltersPill && (
                    <MoreFiltersModal
                        filterGroups={filterGroups}
                        sliceAt={0}
                        {...sharedProps}
                    />
                )}
                {showMoreFiltersPill && (
                    <MoreFiltersModal
                        filterGroups={filterGroups}
                        sliceAt={displayNumGroupsAsPills}
                        {...sharedProps}
                    />
                )}
            </>
        );
    };

    return (
        <div className="FilterBar">
            <div
                className="FilterBar__content"
                ref={filterBarContentRef}
                onMouseEnter={hoverInit}
                onMouseLeave={handleOnMouseLeave}
                onBlur={handleOnBlur}
                onScroll={handleScroll}
                role="presentation"
            >
                {isLoading ? (
                    <FilterBarLoading
                        placeholdersCount={displayNumGroupsAsPills + 1}
                    />
                ) : (
                    <>
                        {displayMoreFiltersPillLeft && renderFiltersPill()}
                        {topFilterGroups.map(({groupInfo, filters}, index) => (
                            <FilterGroupPill
                                key={groupInfo.id}
                                label={groupInfo.name}
                                secondaryLabel={''} // TODO: Get this key from filter service
                                groupId={groupInfo.id}
                                filters={filters}
                                scrolling={isScrolling}
                                rank={index}
                                {...sharedProps}
                            />
                        ))}
                        {!displayMoreFiltersPillLeft && renderFiltersPill()}
                    </>
                )}

                <div
                    className={`${filtersPaginationClassnames} FilterBar__pagination--left`}
                    onClick={() => {
                        handlePaginationClick('left');
                    }}
                    role="button"
                    aria-hidden
                >
                    <SVGIcon markup={CHEVRON_LEFT__16} />
                </div>

                <div
                    className={`${filtersPaginationClassnames} FilterBar__pagination--right`}
                    onClick={() => {
                        handlePaginationClick('right');
                    }}
                    role="button"
                    aria-hidden
                >
                    <SVGIcon markup={CHEVRON_RIGHT__16} />
                </div>
            </div>
        </div>
    );
}
